/**
 * EVV (Electronic Visit Verification) Modal Component
 * Handles check-in/check-out with geolocation
 */

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Fade,
} from '@mui/material'
import {
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  getCurrentLocation,
  evvCheckIn,
  evvCheckOut,
} from '@/services/mockContractsApi'
import { Session } from '@/types/contracts'

interface EvvModalProps {
  open: boolean
  onClose: () => void
  session: Session
  mode: 'check-in' | 'check-out'
  onSuccess: (updatedSession: Session) => void
}

const EvvModal: React.FC<EvvModalProps> = ({
  open,
  onClose,
  session,
  mode,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [location, setLocation] = useState<{
    lat: number
    lng: number
    address: string
  } | null>(null)
  const [locationError, setLocationError] = useState<string>('')
  const [manualOverride, setManualOverride] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    if (open) {
      fetchLocation()
    }
  }, [open])

  const fetchLocation = async () => {
    setVerifying(true)
    setLocationError('')
    try {
      const loc = await getCurrentLocation()
      setLocation(loc)
      setManualOverride(false)
    } catch (error: any) {
      setLocationError(error.message || 'Unable to retrieve location')
      setManualOverride(true)
    } finally {
      setVerifying(false)
    }
  }

  const handleSubmit = async () => {
    if (manualOverride && !overrideReason.trim()) {
      setLocationError(
        'Override reason is required when geolocation is unavailable'
      )
      return
    }

    setLoading(true)
    try {
      let updatedSession: Session

      if (mode === 'check-in') {
        updatedSession = await evvCheckIn(session.id, location || undefined)
        if (manualOverride) {
          // In a real app, this would be stored with the EVV record
          console.log('Manual override reason:', overrideReason)
        }
      } else {
        updatedSession = await evvCheckOut(session.id, location || undefined)
      }

      onSuccess(updatedSession)
      onClose()
    } catch (error: any) {
      setLocationError(error.message || 'Failed to process EVV')
    } finally {
      setLoading(false)
    }
  }

  const renderLocationInfo = () => {
    if (verifying) {
      return (
        <Box display="flex" alignItems="center" gap={2} p={3}>
          <CircularProgress size={24} />
          <Typography>Verifying location...</Typography>
        </Box>
      )
    }

    if (location) {
      return (
        <Fade in>
          <Box>
            <Box
              sx={{
                p: 2,
                backgroundColor: 'success.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'success.200',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  color="success.dark"
                  fontWeight={600}
                >
                  Location Verified
                </Typography>
              </Box>
              <Box display="flex" alignItems="start" gap={1}>
                <LocationIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2">{location.address}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Lat: {location.lat.toFixed(6)}, Lng:{' '}
                    {location.lng.toFixed(6)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Mock map placeholder */}
            <Box
              sx={{
                mt: 2,
                height: 200,
                backgroundColor: 'grey.100',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  opacity: 0.1,
                }}
              />
              <Box position="absolute">
                <LocationIcon sx={{ fontSize: 48, color: 'error.main' }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                Map Preview (Demo)
              </Typography>
            </Box>
          </Box>
        </Fade>
      )
    }

    if (locationError) {
      return (
        <Fade in>
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {locationError}
              </Typography>
              <Typography variant="caption">
                Manual override will be applied. Manager approval may be
                required for claim submission.
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Override Reason *"
              placeholder="Explain why geolocation is unavailable..."
              multiline
              rows={3}
              value={overrideReason}
              onChange={e => setOverrideReason(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Button
              startIcon={<MyLocationIcon />}
              onClick={fetchLocation}
              variant="outlined"
              size="small"
            >
              Retry Location
            </Button>
          </Box>
        </Fade>
      )
    }

    return null
  }

  const canSubmit = () => {
    if (loading || verifying) return false
    if (manualOverride && !overrideReason.trim()) return false
    return true
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <LocationIcon color="primary" />
            <Typography variant="h6">
              {mode === 'check-in' ? 'Clock In' : 'Clock Out'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Session Information
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                Patient:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {/* You would pass patient info as a prop in real implementation */}
                Session {session.id}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                Scheduled:
              </Typography>
              <Typography variant="body2">
                {format(new Date(session.scheduledAt), 'MMM dd, yyyy h:mm a')}
              </Typography>
            </Box>
            {mode === 'check-out' && session.evv.checkInAt && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Checked In:
                </Typography>
                <Typography
                  variant="body2"
                  color="success.main"
                  fontWeight={600}
                >
                  {format(new Date(session.evv.checkInAt), 'h:mm a')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {renderLocationInfo()}

        {manualOverride && (
          <Fade in>
            <Box mt={2}>
              <Chip
                icon={<WarningIcon />}
                label="Manual Override Required"
                color="warning"
                size="small"
                variant="outlined"
              />
            </Box>
          </Fade>
        )}

        {locationError && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>PHI Notice:</strong> This session will be flagged for
              manager review due to manual override.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit()}
          startIcon={
            loading ? <CircularProgress size={16} /> : <CheckCircleIcon />
          }
        >
          {mode === 'check-in' ? 'Clock In' : 'Clock Out'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EvvModal
